import React from 'react';
import { Form, Formik, FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { Button, Divider, Grid, Typography, useMediaQuery } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
import { AccountData, AccountService, Language, PassType, RedeemableOnItemInput } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { useGetAccountAuthenticated } from '../../session/swr/useGetAccountAuthenticated';
import { dynamicLabelSchema } from '../../experienceSettings/sections/informationSection/dynamicLabelSchema';
import { getOrderPages, getPassOrderPages } from '../../utils/AccountUtils';
import { FormTextField } from '../../experienceSettings/formComponents/FormTextField';
import { getCurrencySymbol } from '../../utils/utils';
import { useLanguage } from '../../common/GeneralUtils';
import { findMultipleTicketsUiObject } from '../../common/multipleTicketsUtils';
import { RedeemableOnMultiSelect } from './sections/RedeemableOnMultiSelect';
import { mb } from '../../common/theme/mbTokens';

const findFirstError = (errors: FormikErrors<any>): string | undefined => {
  if (!errors) return undefined;
  if (typeof errors === 'string') return errors;
  if (Array.isArray(errors)) {
    for (const entry of errors) {
      const nested = findFirstError(entry as FormikErrors<any>);
      if (nested) return nested;
    }
    return undefined;
  }
  for (const key of Object.keys(errors)) {
    const nested = findFirstError((errors as Record<string, any>)[key]);
    if (nested) return nested;
  }
  return undefined;
};

type CreateStandardPassValues = {
  label: { [key in Language]?: string };
  price: number | undefined;
  num_punches: number | undefined;
  validity_days: number | undefined;
  redeemable_on: RedeemableOnItemInput[];
};

type CreateStandardPassFormProps = {
  passType: PassType.PUNCH_BASED | PassType.UNLIMITED_MONTHLY;
  handleBack: () => void;
};

export const CreateStandardPassForm = ({ passType, handleBack }: CreateStandardPassFormProps) => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { account, updateLocalAccount } = useGetAccountAuthenticated({});
  const { t } = useTranslation();
  const { authState } = useAuth();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const textBoxWidth = isSmallScreen ? 300 : 500;
  const currentAccount = (account || authState.account) as unknown as AccountData;
  const currencySymbol = getCurrencySymbol(currentAccount);
  const languages = currentAccount?.customer_facing_widgets_languages || [];
  const language = useLanguage();
  const orderPagesById = React.useMemo(() => {
    const map = new Map<string, ReturnType<typeof getOrderPages>[number]>();
    for (const page of getOrderPages(currentAccount)) map.set(page.id, page);
    return map;
  }, [currentAccount]);

  const validationSchema = yup.object().shape({
    label: yup.object().shape(dynamicLabelSchema(t, languages, true)),
    price: yup.number().positive(t('editOrderPage.yup.hasToBePoitive')).required(t('editOrderPage.yup.required')),
    num_punches: yup
      .number()
      .transform((val, orig) => (orig === '' || orig === null ? undefined : val))
      .positive(t('editOrderPage.yup.hasToBePoitive'))
      .integer()
      .optional(),
    validity_days: yup
      .number()
      .positive(t('editOrderPage.yup.hasToBePoitive'))
      .integer()
      .required(t('editOrderPage.yup.required')),
    redeemable_on: yup.array().of(
      yup.object().shape({
        order_page_id: yup.string().required(),
        ticket_type_id: yup.string().when('order_page_id', (orderPageId: any, schema: yup.StringSchema) => {
          const page = orderPagesById.get(orderPageId as string);
          if (page && findMultipleTicketsUiObject(page)) {
            const workshopLabel = page.label?.[language] || '';
            return schema.required(t('passes.ticket_type_required', { workshop: workshopLabel }));
          }
          return schema.optional();
        }),
      }),
    ),
  });

  const initialValues: CreateStandardPassValues = {
    label: languages.reduce((acc, lang) => ({ ...acc, [lang]: '' }), {}),
    price: undefined,
    num_punches: undefined,
    validity_days: undefined,
    redeemable_on: [],
  };

  const onSubmit = async (values: CreateStandardPassValues) => {
    const updatedAccount = (await AccountService.createPassOrderPage({
      label: values.label as any,
      price: values.price!,
      pass_type: passType,
      num_punches: values.num_punches,
      validity_days: values.validity_days!,
      redeemable_on: values.redeemable_on,
    })) as unknown as AccountData;
    await updateLocalAccount(updatedAccount);
    const passOrderPages = getPassOrderPages(updatedAccount);
    const newId = passOrderPages[passOrderPages.length - 1].id;
    posthog.capture('Created pass order page', { passOrderPageId: newId, passType });
    navigate(`/dashboard/pass-settings/${newId}#information`);
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ values, isSubmitting, setFieldValue, errors, submitCount }) => {
        const firstError = submitCount > 0 ? findFirstError(errors) : undefined;
        return (
          <Form style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', minHeight: 0 }}>
            <Grid container flexDirection="column" sx={{ p: mb.layout.cardPadding }}>
              <Grid container my={3} gap={2} flexDirection="column">
                <Typography variant="h5">{t('editOrderPage.basicInformation')}</Typography>
                <Divider />
                {languages.map((lang) => (
                  <Grid key={lang}>
                    <FormTextField
                      width={textBoxWidth}
                      label={t(`editOrderPage.passNameLang.${lang}`)}
                      fieldName={`label.${lang}`}
                      disabled={isSubmitting}
                      isFieldUpdate={false}
                    />
                  </Grid>
                ))}
              </Grid>

              <Grid container my={3} gap={2} flexDirection="column">
                <Typography variant="h5">{t('editOrderPage.pricing')}</Typography>
                <Divider />
                <FormTextField
                  type="number"
                  width={200}
                  fieldName="price"
                  label={t('passes.price')}
                  endAdornment={currencySymbol}
                  disabled={isSubmitting}
                  isFieldUpdate={false}
                />
                <FormTextField
                  type="number"
                  width={200}
                  fieldName="num_punches"
                  label={t('passes.num_punches')}
                  disabled={isSubmitting}
                  isFieldUpdate={false}
                />
                <FormTextField
                  type="number"
                  width={200}
                  fieldName="validity_days"
                  label={t('passes.validity_days')}
                  disabled={isSubmitting}
                  isFieldUpdate={false}
                />
              </Grid>

              <Grid container my={3} gap={2} flexDirection="column">
                <Typography variant="h5">{t('passes.redeemable_on')}</Typography>
                <Divider />
                <RedeemableOnMultiSelect
                  value={values.redeemable_on}
                  onChange={(items) => setFieldValue('redeemable_on', items)}
                  account={currentAccount}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid container justifyContent="space-between" mt={2} alignItems="center" gap={2}>
                <Button variant="outlined" onClick={handleBack}>
                  {t('back')}
                </Button>
                <Grid container alignItems="center" gap={2} justifyContent="flex-end" sx={{ flex: 1, minWidth: 0 }}>
                  {firstError && (
                    <Typography variant="body2" color="error" sx={{ textAlign: 'end' }}>
                      {firstError}
                    </Typography>
                  )}
                  <LoadingButton size="large" loading={isSubmitting} type="submit" variant="contained" color="primary">
                    {t('passes.create_pass_product')}
                  </LoadingButton>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
